/**
 * Ritual Join Request Status Notification Component
 * Displays when a user's join request is accepted or rejected
 * Shows ritual info with cover image
 */

import { EllipsisVertical } from "lucide-react"
import { useNavigate } from "react-router-dom"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { getRitualTypeLabel } from "@/features/rituals/types"
import { formatDuration } from "@/features/rituals/utils/formatDuration"
import { formatNotificationTime } from "@/utils/formatTime"
import type { Notification } from "../services/notification.service"
import { RitualNotificationCard } from "./RitualNotificationCard"

interface RitualJoinRequestStatusNotificationProps {
  notification: Notification
  onRead?: () => void
  onUnread?: () => void
}

interface RitualJoinRequestStatusNotificationData {
  ritualId?: string
  ritualName?: string
  accepterId?: string
  accepterName?: string
  accepterUsername?: string
  rejecterId?: string
  rejecterName?: string
  rejecterUsername?: string
  reason?: string
  requestId?: string
  actionTaken?: "accepted" | "rejected"
  actionTakenByName?: string
  ritualCoverImageUrl?: string | null
  isPaid?: boolean
  fee?: number | null
  duration?: string
  ritualType?: string
  durationType?: string
}

/**
 * Get the display image URL for a user
 * Prioritizes cover photo from profile_photos, falls back to profilePictureUrl
 */
function getUserDisplayImage(user: Notification["triggeredByUser"]): string | undefined {
  if (!user) return undefined
  if (user.profile_photos && user.profile_photos.length > 0) {
    const coverPhoto = user.profile_photos.find(p => p.isCover || p.is_cover_image)
    if (coverPhoto?.filePath) {
      return coverPhoto.filePath
    }
  }
  return user.profilePictureUrl ?? undefined
}

export function RitualJoinRequestStatusNotification({
  notification,
  onRead,
  onUnread,
}: RitualJoinRequestStatusNotificationProps) {
  const navigate = useNavigate()

  const data = notification.data as RitualJoinRequestStatusNotificationData

  const ritualId = data.ritualId
  const isAccepted = notification.action === "join_request_accepted"
  const isRejected = notification.action === "join_request_rejected"
  const isRead = notification.isRead

  if (!notification.triggeredByUser || !notification.data || !ritualId) {
    return null
  }

  const responder = notification.triggeredByUser

  const ritualName = data.ritualName || "a ritual"
  const ritualCoverImageUrl = data.ritualCoverImageUrl
  const isPaid = data.isPaid
  const fee = data.fee
  const duration = data.duration
  const durationType = data.durationType
  const ritualType = data.ritualType

  const displayImageUrl = getUserDisplayImage(responder)
  const displayName = responder.name || responder.username || "Someone"

  const relativeTime = formatNotificationTime(notification.createdAt)

  const ritualTypeLabel = ritualType ? getRitualTypeLabel(ritualType) : ""
  const formattedDuration = formatDuration(duration, durationType)

  const handleAvatarClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(`/profile/${responder.id}`)
    if (!isRead) {
      onRead?.()
    }
  }

  const handleRitualClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    window.location.href = `/rituals/${ritualId}`
    if (!isRead) {
      onRead?.()
    }
  }

  const handleMarkAsRead = () => {
    onRead?.()
  }

  const handleMarkAsUnread = () => {
    onUnread?.()
  }

  return (
    <div
      className={`flex flex-col gap-3 p-4 rounded-md cursor-pointer transition-colors border ${
        !isRead ? "notification-unread" : "notification-read"
      }`}
    >
      {/* Header Row: Avatar, Name, Username, Time, Menu */}
      <div className="relative flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {/* User avatar */}
          <div
            className="w-12 h-12 rounded-full overflow-hidden bg-card flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={handleAvatarClick}
          >
            {displayImageUrl ? (
              <img src={displayImageUrl} alt={displayName} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-primary bg-primary/10 text-lg">
                {displayName[0]}
              </div>
            )}
          </div>

          {/* Name, Username, Time */}
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-primary text-[14px]">{displayName}</span>
              {responder.username && (
                <span className="text-stone text-[14px]">@{responder.username}</span>
              )}
              <span className="text-stone/50 text-[14px]">{relativeTime}</span>
            </div>

            {/* Message Row */}
            <div className="text-[14px] text-primary leading-[25px]">
              {isAccepted ? (
                <>
                  <span className="font-semibold">{displayName}</span> accepted your request to join{" "}
                  <span className="font-medium cursor-pointer" onClick={handleRitualClick}>
                    {ritualName}
                  </span>
                </>
              ) : isRejected ? (
                <>
                  <span className="font-semibold">{displayName}</span> declined your request to join{" "}
                  <span className="font-medium cursor-pointer" onClick={handleRitualClick}>
                    {ritualName}
                  </span>
                </>
              ) : null}
              {isRejected && data.reason && (
                <span className="text-stone text-[12px]">: {data.reason}</span>
              )}
            </div>
          </div>
        </div>

        {/* Menu */}
        <div className="absolute -top-2 -right-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="w-8 h-8 rounded-full flex items-center justify-center transition-colors cursor-pointer"
                onClick={e => e.stopPropagation()}
              >
                <EllipsisVertical className="w-4 h-4 text-stone" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40">
              {isRead ? (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsUnread()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as unread
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleMarkAsRead()
                  }}
                  className="text-[11px] cursor-pointer"
                >
                  Mark as read
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <RitualNotificationCard
        coverImageUrl={ritualCoverImageUrl}
        title={ritualName}
        isPaid={isPaid}
        fee={fee}
        ritualTypeLabel={ritualTypeLabel || "Self Guided"}
        duration={formattedDuration}
        onClick={handleRitualClick}
      />
    </div>
  )
}
